import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fbShareUrl, openFbShare, likedSet, saveLiked, uploadToGallery } from "@/lib/gallery";
import { toast } from "sonner";
import { Heart, Share2, X, ChevronLeft, ChevronRight, Camera, MapPin, Calendar, Play, Upload, MessageSquare, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PB_LOGO = "/brand/pb-logo.png";
const thumbOf = (m) => m.media_type === "video" && m.url.includes("/video/upload/")
  ? m.url.replace("/video/upload/", "/video/upload/so_0,w_800/").replace(/\.[^./]+$/, ".jpg")
  : m.url;

function Lightbox({ media, idx, setIdx, onClose, liked, toggleLike, albumId }) {
  const m = media[idx];
  const prev = useCallback(() => setIdx((i) => (i - 1 + media.length) % media.length), [media.length, setIdx]);
  const next = useCallback(() => setIdx((i) => (i + 1) % media.length), [media.length, setIdx]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);
  if (!m) return null;
  const isLiked = liked.has(m.id);
  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex flex-col" data-testid="gallery-lightbox">
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">{idx + 1} / {media.length}{m.caption ? ` — ${m.caption}` : ""}</span>
        <div className="flex items-center gap-2">
          <button data-testid="lightbox-like-btn" onClick={() => toggleLike(m)}
            className={`flex items-center gap-1.5 border px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest transition-colors ${isLiked ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/10" : "border-zinc-700 text-zinc-300 hover:border-[#A3E635]"}`}>
            <Heart className={`w-4 h-4 ${isLiked ? "fill-[#A3E635]" : ""}`} /> {m.likes}
          </button>
          <button data-testid="lightbox-share-btn" onClick={() => openFbShare(fbShareUrl(albumId, m.id))}
            className="flex items-center gap-1.5 border border-[#1877F2]/70 text-[#7db4f5] px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest hover:bg-[#1877F2]/20 transition-colors">
            <Facebook className="w-4 h-4" /> Share
          </button>
          <button data-testid="lightbox-close-btn" onClick={onClose}
            className="w-9 h-9 border border-zinc-700 text-zinc-300 hover:border-white flex items-center justify-center transition-colors"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-14 pb-6 min-h-0">
        <button data-testid="lightbox-prev-btn" onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 border border-zinc-700 bg-black/60 text-zinc-300 hover:border-[#A3E635] hover:text-[#A3E635] flex items-center justify-center transition-colors"><ChevronLeft className="w-6 h-6" /></button>
        {m.media_type === "video" ? (
          <video key={m.id} src={m.url} controls autoPlay className="max-h-full max-w-full" data-testid="lightbox-video" />
        ) : (
          <img key={m.id} src={m.url} alt={m.caption || ""} className="max-h-full max-w-full object-contain" data-testid="lightbox-image" />
        )}
        <button data-testid="lightbox-next-btn" onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 border border-zinc-700 bg-black/60 text-zinc-300 hover:border-[#A3E635] hover:text-[#A3E635] flex items-center justify-center transition-colors"><ChevronRight className="w-6 h-6" /></button>
      </div>
    </div>
  );
}

export default function GalleryAlbum() {
  const { aid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lbIdx, setLbIdx] = useState(null);
  const [liked, setLiked] = useState(likedSet());
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(0);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    axios.get(`${API}/gallery/albums/${aid}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.album.title} — Pickle Bullpen Gallery`;
    }).catch(() => setError("This gallery isn't available."));
  }, [aid]);
  useEffect(() => { load(); }, [load]);

  const toggleLike = (m) => {
    const isLiked = liked.has(m.id);
    const nl = new Set(liked);
    isLiked ? nl.delete(m.id) : nl.add(m.id);
    setLiked(nl); saveLiked(nl);
    setData((d) => ({ ...d, media: d.media.map((x) => x.id === m.id ? { ...x, likes: Math.max(0, x.likes + (isLiked ? -1 : 1)) } : x) }));
    axios.post(`${API}/gallery/media/${m.id}/like`, { delta: isLiked ? -1 : 1 }).catch(() => {});
  };

  const postComment = async () => {
    const text = comment.trim();
    if (!text) return;
    try {
      const { data: c } = await api.post(`/gallery/albums/${aid}/comments`, { text });
      setData((d) => ({ ...d, comments: [...d.comments, c] }));
      setComment("");
      toast.success("Comment posted");
    } catch { toast.error("Couldn't post the comment — are you signed in?"); }
  };

  const submitPhotos = async (files) => {
    for (const f of Array.from(files).slice(0, 10)) {
      try {
        setUploading(1);
        const up = await uploadToGallery(f, setUploading);
        await api.post(`/gallery/albums/${aid}/submit`, { url: up.url, media_type: up.media_type });
      } catch { toast.error(`Upload failed for ${f.name}`); }
    }
    setUploading(0);
    toast.success("Submitted! Your shots go live once the Bullpen team approves them. 📸");
  };

  if (error) return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-500 flex flex-col items-center justify-center gap-4 font-display uppercase text-xl">
      {error}
      <Link to="/gallery" className="text-[#A3E635] text-sm border border-[#A3E635]/50 px-4 py-2">← Back to Gallery Search</Link>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-[#0a0a0a] text-zinc-500 flex items-center justify-center animate-pulse">Loading gallery…</div>;

  const { album, media, comments } = data;
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="gallery-album-page">
      <header className="relative">
        <div className="h-[42vh] min-h-[280px] overflow-hidden bg-zinc-950">
          {album.cover_url && <img src={album.cover_url} alt="" className="w-full h-full object-cover opacity-60" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-black/50 to-black/40" />
        </div>
        <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-4">
          <Link to="/gallery" data-testid="album-back-link" className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-zinc-300 hover:text-[#A3E635] transition-colors">
            <ChevronLeft className="w-4 h-4" /> Gallery Search
          </Link>
          <img src={PB_LOGO} alt="" className="w-10 h-10 object-contain" />
        </div>
        <div className="absolute bottom-0 inset-x-0 px-6 pb-6 max-w-6xl mx-auto">
          <div className="text-[10px] font-black uppercase tracking-[0.35em] text-[#22D3EE] mb-2">Tournament Gallery</div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl leading-none" data-testid="album-title">{album.title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-[11px] uppercase tracking-widest text-zinc-400">
            {album.date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{album.date}</span>}
            {album.location && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{album.location}</span>}
            <span className="flex items-center gap-1.5 text-[#A3E635]"><Camera className="w-3.5 h-3.5" />{media.length} photos & videos</span>
            <button data-testid="album-share-btn" onClick={() => openFbShare(fbShareUrl(album.id))}
              className="flex items-center gap-1.5 border border-[#1877F2]/70 text-[#7db4f5] px-3 py-1.5 font-bold hover:bg-[#1877F2]/20 transition-colors">
              <Facebook className="w-3.5 h-3.5" /> Share this gallery
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <p className="text-zinc-500 text-sm">{album.description || "Tap any shot to view it full screen — drop a ❤️ and share your favorites."}</p>
          {user ? (
            <>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden"
                onChange={(e) => e.target.files.length && submitPhotos(e.target.files)} data-testid="album-submit-input" />
              <button data-testid="album-submit-btn" disabled={!!uploading} onClick={() => fileRef.current?.click()}
                className="flex items-center gap-2 border border-[#A3E635]/60 text-[#A3E635] text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-[#A3E635]/10 transition-colors disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? `Uploading ${uploading}%…` : "Submit your photos"}
              </button>
            </>
          ) : (
            <a href="/auth" data-testid="album-signin-submit" className="flex items-center gap-2 border border-zinc-700 text-zinc-400 text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:border-[#A3E635] hover:text-[#A3E635] transition-colors">
              <Upload className="w-4 h-4" /> Sign in to submit your photos
            </a>
          )}
        </div>

        {media.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-14 text-center text-zinc-600 font-display uppercase" data-testid="album-empty">
            Photos are on the way — check back soon.
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4" data-testid="album-masonry">
            {media.map((m, i) => (
              <div key={m.id} className="break-inside-avoid mb-4 group relative cursor-pointer border border-zinc-900 hover:border-[#A3E635]/70 transition-colors animate-fade-up"
                style={{ animationDelay: `${Math.min(i, 12) * 50}ms` }}
                onClick={() => setLbIdx(i)} data-testid={`album-media-${m.id}`}>
                <img src={thumbOf(m)} alt={m.caption || ""} loading="lazy" className="w-full group-hover:opacity-90 transition-opacity" />
                {m.media_type === "video" && (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 border border-white/40 bg-black/60 backdrop-blur flex items-center justify-center"><Play className="w-5 h-5 text-[#A3E635]" /></span>
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <button data-testid={`media-like-${m.id}`} onClick={(e) => { e.stopPropagation(); toggleLike(m); }}
                    className={`flex items-center gap-1 text-[11px] font-bold ${liked.has(m.id) ? "text-[#A3E635]" : "text-white"}`}>
                    <Heart className={`w-4 h-4 ${liked.has(m.id) ? "fill-[#A3E635]" : ""}`} /> {m.likes}
                  </button>
                  <button data-testid={`media-share-${m.id}`} onClick={(e) => { e.stopPropagation(); openFbShare(fbShareUrl(album.id, m.id)); }}
                    className="text-white hover:text-[#7db4f5]"><Share2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <section className="mt-12 max-w-3xl" data-testid="album-comments">
          <h2 className="flex items-center gap-2 text-base md:text-lg font-display font-black uppercase mb-4">
            <MessageSquare className="w-5 h-5 text-[#22D3EE]" /> Comments ({comments.length})
          </h2>
          {comments.length === 0 && <p className="text-zinc-600 text-sm mb-4">No comments yet — be the first to relive it.</p>}
          <div className="space-y-3 mb-6">
            {comments.map((c) => (
              <div key={c.id} className="border border-zinc-900 bg-zinc-950 px-4 py-3" data-testid={`comment-${c.id}`}>
                <div className="text-[10px] font-black uppercase tracking-widest text-[#A3E635] mb-1">{c.author_name}</div>
                <div className="text-sm text-zinc-300">{c.text}</div>
              </div>
            ))}
          </div>
          {user ? (
            <div className="flex gap-2">
              <input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && postComment()}
                placeholder="Share a memory from this tournament…"
                className="flex-1 bg-zinc-950 border border-zinc-800 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#22D3EE] transition-colors" />
              <button data-testid="comment-post-btn" onClick={postComment}
                className="bg-[#22D3EE] text-black text-[10px] font-black uppercase tracking-widest px-5 hover:opacity-90 transition-opacity">Post</button>
            </div>
          ) : (
            <a href="/auth" data-testid="comment-signin" className="block border border-dashed border-zinc-800 px-4 py-4 text-center text-sm text-zinc-500 hover:border-[#22D3EE] hover:text-[#22D3EE] transition-colors">
              Comments are for Bullpen members — sign in or join free to leave one.
            </a>
          )}
        </section>
      </main>

      {lbIdx !== null && (
        <Lightbox media={media} idx={lbIdx} setIdx={setLbIdx} onClose={() => setLbIdx(null)}
          liked={liked} toggleLike={toggleLike} albumId={album.id} />
      )}
    </div>
  );
}
